import { PriceGroup } from '$shared/components';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    StyledDetails,
    StyledName,
    StyledBrand,
    StyledSwipableContainer,
    StyledColorsText,
    StyledImagesContainer,
    StyledProductCardLink,
    StyledPrimaryImageWrapper,
    StyledSecondaryImageWrapper,
    StyledProductTagsContainer,
    StyledSubtitle,
    StyledTitlesWrapper,
    StyledTitle,
    StyledAttributesWrapper,
    StyledAttributes,
    StyledHoverIndicatorWrapper,
    StyledDots,
    StyledDot,
} from './styled';
import Arrow from '$icons/arrow-up-right.svg';
import { queries, Spacer } from '~/theme';
import { useFranchise, useGtm, useTranslation, gtmProductMapper } from '~/shared/utils';

import { FormattedAlgoliaProductType } from '~/features/productsList/types';
import { Product } from '~/lib/data-contract';
import { WrappedInsightsClient } from 'react-instantsearch-core';
import { ProductImage } from '~/shared/components/ProductImage';
import { useMedia } from 'react-use';
import { VisuallyHidden } from '../VisuallyHidden';
import { useAlgoliaAnalytics, useIsInitialRenderState } from '~/shared/hooks';
import { ProductImpressionTracker } from './components/ProductImpressionTracker/ProductImpressionTracker';
import { ImageProps } from '../Image';
import { ProductTags } from '~/shared/components/ProductTags';
import { Visibility } from '~/shared/utils/jsx';
import { useInView } from 'framer-motion';

export type ProductCardProps = {
    product: FormattedAlgoliaProductType | Product;
    queryId?: string;
    insights?: WrappedInsightsClient;
    index?: number;
    listType: string;
    insideScroll?: boolean;
    isProductListPage?: boolean;
} & Pick<ImageProps, 'onLoadAnimation' | 'skeletonShade'>;

export const ProductCard = ({
    product,
    insights,
    queryId,
    index,
    listType,
    insideScroll = false,
    skeletonShade = 'none',
}: ProductCardProps) => {
    const {
        name,
        subtitle,
        category,
        brand,
        priceDetails,
        images,
        productId,
        productUrl = '',
        colors,
        imageTags,
        color,
    } = product;
    const isMobile = !useMedia(queries.md, false);
    const [isSecondaryImageLoaded, setIsSecondaryImageLoaded] = useState(false);
    const { initialized } = useAlgoliaAnalytics();

    const { translate } = useTranslation();
    const { trackProductClick } = useGtm();

    const { activeMarket } = useFranchise();
    const { isInitialRender } = useIsInitialRenderState();

    const card = useRef<HTMLAnchorElement | null>(null);
    const inView = useInView(card, { once: true });

    const sliderRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const [activeSlide, setActiveSlide] = useState<number>(0);

    const handleCardClick = useCallback(() => {
        trackProductClick(gtmProductMapper([product]), listType, activeMarket?.currency);

        // Algolia Tracking
        // using insights function Algolia will infer important parameters such as:
        // index, positions, and objectIDs
        if (insights && initialized) {
            insights('clickedObjectIDsAfterSearch', {
                eventName: 'Product Clicked',
            });
        }
    }, [activeMarket?.currency, initialized, insights, listType, product, trackProductClick]);

    useEffect(() => {
        if (isMobile && isSecondaryImageLoaded && sliderRef.current) {
            const slides = Array.from(sliderRef.current.children);

            const handleIntersect: IntersectionObserverCallback = (entries) => {
                entries.forEach((entry) => {
                    const slideIndex = slides.indexOf(entry.target as Element);
                    if (entry.isIntersecting) {
                        setActiveSlide(slideIndex);
                    }
                });
            };

            if (observerRef.current) {
                observerRef.current.disconnect();
            }

            const newObserver = new IntersectionObserver(handleIntersect, {
                root: sliderRef.current,
                threshold: 0.5,
            });

            slides.forEach((slide) => newObserver.observe(slide));
            observerRef.current = newObserver;
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, [images, isMobile, isSecondaryImageLoaded]);

    const detailUrl = !queryId ? productUrl : `${productUrl}#queryId=${queryId}`;
    const imageSize = {
        height: 501,
        width: 334,
    };

    const secondaryImage = images?.[1] || null;

    const altText = [name, category, color, productId].filter(Boolean).join(' ');

    return (
        <StyledProductCardLink
            href={detailUrl}
            ref={card}
            aria-label={name}
            onClick={handleCardClick}
            prefetch={false}
        >
            <VisuallyHidden>{name}</VisuallyHidden>
            <StyledImagesContainer>
                <StyledSwipableContainer ref={sliderRef}>
                    <StyledPrimaryImageWrapper>
                        <ProductImage
                            {...imageSize}
                            image={images?.[0]}
                            onLoadAnimation="none"
                            loading={
                                isInitialRender && index !== undefined && index < 8
                                    ? 'eager'
                                    : 'lazy'
                            }
                            skeletonShade={skeletonShade}
                            alt={altText}
                            style={{
                                objectFit: 'contain',
                                width: '100%',
                                height: '100%',
                                aspectRatio: '2/3',
                            }}
                        />
                    </StyledPrimaryImageWrapper>
                    {secondaryImage ? (
                        <StyledSecondaryImageWrapper
                            insideScroll={insideScroll}
                            isImageLoaded={isSecondaryImageLoaded}
                        >
                            <ProductImage
                                {...imageSize}
                                image={secondaryImage}
                                alt={altText}
                                onLoadAnimation="none"
                                onLoadCallback={() => setIsSecondaryImageLoaded(true)}
                                loading="lazy"
                                style={{
                                    objectFit: 'contain',
                                    width: '100%',
                                    height: '100%',
                                    aspectRatio: '2/3',
                                }}
                            />
                        </StyledSecondaryImageWrapper>
                    ) : null}
                </StyledSwipableContainer>
                {isMobile && isSecondaryImageLoaded ? (
                    <StyledDots>
                        {[...Array(2)].map((_, index) => (
                            <StyledDot key={index} isActive={index === activeSlide} />
                        ))}
                    </StyledDots>
                ) : null}
                <StyledProductTagsContainer position="top">
                    <ProductTags productTags={imageTags} selectFrom="top" isCampaign />
                    <Spacer space="1" />
                    <ProductTags productTags={imageTags} selectFrom="top" />
                </StyledProductTagsContainer>
            </StyledImagesContainer>
            <StyledDetails insideScroll={insideScroll}>
                {brand ? (
                    <StyledBrand title={name} aria-label={name}>
                        {brand}
                    </StyledBrand>
                ) : null}
                <StyledName title={name} aria-label={name}>
                    <StyledTitlesWrapper>
                        <StyledTitle variant="body">{name}</StyledTitle>
                        {subtitle ? (
                            <StyledSubtitle variant="caption">{subtitle}</StyledSubtitle>
                        ) : null}
                    </StyledTitlesWrapper>
                    {colors ? (
                        <Visibility.VisibleFrom md>
                            <StyledColorsText variant="bodySm">
                                {colors} {translate('commerce.colors')}
                            </StyledColorsText>
                        </Visibility.VisibleFrom>
                    ) : null}
                </StyledName>
                <StyledAttributesWrapper>
                    <StyledAttributes>
                        {priceDetails && <PriceGroup {...priceDetails} />}
                        <StyledProductTagsContainer position="bottom">
                            <ProductTags
                                productTags={imageTags}
                                selectFrom="bottom"
                                layout="list"
                            />
                        </StyledProductTagsContainer>
                    </StyledAttributes>
                    <StyledHoverIndicatorWrapper aria-hidden="true">
                        <Arrow />
                    </StyledHoverIndicatorWrapper>
                </StyledAttributesWrapper>
                {colors ? (
                    <Visibility.HiddenFrom md>
                        <Spacer space="2" />
                        <StyledColorsText variant="caption">
                            {colors} {translate('commerce.colors')}
                        </StyledColorsText>
                    </Visibility.HiddenFrom>
                ) : null}
            </StyledDetails>
            <ProductImpressionTracker inView={inView} product={product} />
        </StyledProductCardLink>
    );
};
